import { useTranslation } from 'react-i18next';
import { isSupportedLocale, DEFAULT_LOCALE } from '@swiss-magic/i18n';
import { PICKABLE_LOCALES, LOCALE_LABELS } from '@/lib/i18n';

// Per-invite language picker shared by director invites (admin) and
// player invites. Both call sites previously inlined the same select+
// label+hint markup; centralising avoids drift when PICKABLE_LOCALES
// changes.

interface InviteLocaleSelectProps {
  value: string;
  onChange: (locale: string) => void;
  disabled?: boolean;
  labelText: string;
  hintText: string;
}

export function InviteLocaleSelect({
  value,
  onChange,
  disabled,
  labelText,
  hintText,
}: InviteLocaleSelectProps) {
  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor="invite-locale-select"
        className="text-sm font-medium"
      >
        {labelText}
      </label>
      <select
        id="invite-locale-select"
        data-testid="invite-locale-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm dark:bg-input/30"
      >
        {PICKABLE_LOCALES.map((locale) => (
          <option key={locale} value={locale}>
            {LOCALE_LABELS[locale]}
          </option>
        ))}
      </select>
      <p className="text-xs text-muted-foreground">{hintText}</p>
    </div>
  );
}

// Default the per-invite picker to the inviting user's current UI
// language when it's user-pickable. xx-XX (the smoke catalog) falls
// through to DEFAULT_LOCALE so the test locale never escapes as a
// real outbound email locale.
export function useDefaultInviteLocale(): string {
  const { i18n } = useTranslation();
  return isSupportedLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE;
}
